import { useContext, ReactNode } from 'react';
import { defineMessages, useIntl } from 'dibs-react-intl';

import { NftProfileSectionCreations } from './NftProfileSections/NftProfileSectionCreations';
import { NftProfileSectionDrafts } from './NftProfileSections/NftProfileSectionDrafts';
import { NftProfileSectionCollection } from './NftProfileSections/NftProfileSectionCollection';
import { NftProfileSectionAuctions } from './NftProfileSections/NftProfileSectionAuctions';
import { NftProfileSectionLiveBids } from './NftProfileSections/NftProfileSectionLiveBids';
import { NftProfileSectionLostBids } from './NftProfileSections/NftProfileSectionLostBids';
import { NftProfileImportCollectionList } from './NftProfileSections/importCollection/NftProfileImportCollectionList';
import { NftProfileImportCollectionView } from './NftProfileSections/importCollection/NftProfileImportCollectionView';

import { ServerVarsContext } from '../../../utils/ServerVarsContext';
import { collectorTabConfig, TabConfig } from '../../helpers/collectorTabConfig';
import { useCreatorSectionConfig } from '../../helpers/importCollectionsConfig';

import { NftProfileCollection_viewer$data as ViewerType } from './__generated__/NftProfileCollection_viewer.graphql';

const messages = defineMessages({
    creations: {
        id: 'nftProfile.auctionSection.yourCreations',
        defaultMessage: 'Creations',
    },
    drafts: {
        id: 'nftProfile.auctionSection.drafts',
        defaultMessage: 'Drafts',
    },
    collection: {
        id: 'nftProfile.auctionSection.collection',
        defaultMessage: 'Collection',
    },
    forSale: {
        id: 'nftProfile.auctionSection.forSale',
        defaultMessage: 'For Sale',
    },
    bids: {
        id: 'nftProfile.auctionSection.bids',
        defaultMessage: 'Bids',
    },
    liveAuctions: {
        id: 'nftProfile.auctionSection.liveAuctions',
        defaultMessage: 'Live Auctions',
    },
    lostAuctions: {
        id: 'nftProfile.auctionSection.lostAuctions',
        defaultMessage: 'Lost Auctions',
    },
});

export type TabNames = 'CREATOR_DRAFTS_TAB' | 'CREATOR_ITEMS_TAB' | 'COLLECTION_TAB' | 'BIDS_TAB';

export type TabRoute = TabConfig & {
    name: TabNames;
    title?: string;
    sections: {
        name: string;
        title?: string;
        node: ReactNode;
        isVisible: boolean;
    }[];
    totalResults: number;
    navPath?: string;
};

/*
 * using config approach because mobile and desktop layouts are semantically different
 * so individual section nodes have to be available (see NftCollectorRespLayout mobile and desktop views)
 */
export function useNftCollectorTabConfig({
    viewer,
    enableDrafts = false,
    isCollectionPending,
}: {
    viewer: ViewerType;
    enableDrafts?: boolean | null;
    isCollectionPending?: boolean;
}): TabRoute[] {
    const intl = useIntl();
    const isMobile = !!useContext(ServerVarsContext).isMobile;
    const creatorSectionConfig = useCreatorSectionConfig(viewer);

    const [draftsTab, creationsTab, collectionTab, bidsTab] = collectorTabConfig(
        viewer,
        !!enableDrafts
    );

    const { draftItems, collection, liveAuctions, liveBids, lostBids } = viewer;

    return [
        {
            ...draftsTab,
            name: 'CREATOR_DRAFTS_TAB',
            title: intl.formatMessage(messages.drafts),
            sections: [
                {
                    name: 'CREATOR_ITEMS_SECTION',
                    node: <NftProfileSectionDrafts viewer={viewer} />,
                    isVisible: !!draftItems?.edges?.length && !!enableDrafts,
                },
            ],
            totalResults: draftItems?.edges?.length || 0,
        },
        {
            ...creationsTab,
            path: creatorSectionConfig.path || creationsTab.path,
            name: 'CREATOR_ITEMS_TAB',
            title: intl.formatMessage(messages.creations),
            sections: [
                {
                    name: 'CREATOR_ITEMS_SECTION',
                    isVisible: creatorSectionConfig.isVisible,
                    node: (
                        <>
                            {creatorSectionConfig.showImportList ? (
                                <NftProfileImportCollectionList viewer={viewer} />
                            ) : null}
                            {creatorSectionConfig.showCreations ? (
                                <NftProfileSectionCreations
                                    viewer={viewer}
                                    hasImports={creatorSectionConfig.showImportList}
                                />
                            ) : null}
                            {creatorSectionConfig.showImportCollection || isCollectionPending ? (
                                <NftProfileImportCollectionView
                                    viewer={viewer}
                                    showBackNav={creatorSectionConfig.showBackNav}
                                    hasTitleLink={creatorSectionConfig.hasTitleLink}
                                    tokenContractId={creatorSectionConfig.tokenContractImportId}
                                />
                            ) : null}
                        </>
                    ),
                },
            ],
            totalResults: creatorSectionConfig.totalResults,
        },
        {
            ...collectionTab,
            name: 'COLLECTION_TAB',
            title: intl.formatMessage(messages.collection),
            sections: [
                {
                    name: 'COLLECTION_SECTION',
                    title: intl.formatMessage(messages.collection),
                    node: <NftProfileSectionCollection isMobile={isMobile} viewer={viewer} />,
                    isVisible: !!collection?.totalResults,
                },
                {
                    name: 'FOR_SALE_SECTION',
                    title: intl.formatMessage(messages.forSale),
                    node: <NftProfileSectionAuctions isMobile={isMobile} viewer={viewer} />,
                    isVisible: !!liveAuctions?.totalResults,
                },
            ],
            totalResults: (collection?.totalResults || 0) + (liveAuctions?.totalResults || 0),
        },
        {
            ...bidsTab,
            name: 'BIDS_TAB',
            title: intl.formatMessage(messages.bids),
            sections: [
                {
                    name: 'LIVE_BIDS_SECTION',
                    title: intl.formatMessage(messages.liveAuctions),
                    node: <NftProfileSectionLiveBids isMobile={isMobile} viewer={viewer} />,
                    isVisible: !!liveBids?.edges?.length,
                },
                {
                    name: 'LOST_BIDS_SECTION',
                    title: intl.formatMessage(messages.lostAuctions),
                    node: <NftProfileSectionLostBids isMobile={isMobile} viewer={viewer} />,
                    isVisible: !!lostBids?.edges?.length,
                },
            ],
            totalResults: (liveBids?.edges?.length || 0) + (lostBids?.edges?.length || 0),
        },
    ];
}
